import React from 'react';
import clsx from 'clsx';
import testIds from '@smart-tv/e2e/shared/variables';
import Clock from '@smart-tv/components/clock/clock';
import Fade from '@smart-tv/components/fade/fade';
import { LiveTvChannel, StreamType } from '@smart-tv/typings/content';
import PlayerChannels from '../player-channels/player-channels';
import PlayerControls from '../player-controls/player-controls';
import useStream from '../_hooks/use-stream';
import { StreamParams } from '../types';
import usePlayerContent from './use-player-content';
import FeatureIntroPlayer from '@smart-tv/components/feature-intro/feature-intro-player';
import useFeatureIntros from '@smart-tv/hooks/use-feature-intros';

import styles from './player-content.module.scss';
import { useAtomValue } from 'jotai';
import { isErrorDialogOpenAtom } from '@smart-tv/atoms';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { usePlayerContext } from '../_contexts/player-context';
import { playerWaitingAtom, visualOnStreamAtom } from '@smart-tv/components/player/atoms';
import { retrievePersistentData } from '@smart-tv/utils/persistent-data/persistent-data';

export const DESCALED_PLAYER_HEIGHT = 1080;
export const DESCALED_PLAYER_WIDTH = 1225;

interface Props {
  channel?: LiveTvChannel;
  setPlayerNode: (node: HTMLDivElement | null) => void;
  streamParams?: StreamParams;
}

const PlayerContent: React.FC<Props> = ({ channel, setPlayerNode, streamParams }) => {
  const {
    rootRef,
    openControls,
    openChannels,
    handleCloseControls,
    handleCloseChannels,
    handleChannelChange,
    handleClickOutside,
    handleKeyDown,
    handleMouseMove,
    hideControls,
    descalePlayer
  } = usePlayerContent(streamParams);
  const { mapStreamTypeToFeatureIntroLocation } = useFeatureIntros();
  const { streamType } = useStream();
  const { firstTimeLoading } = usePlayerContext();
  const visualOnStream = useAtomValue(visualOnStreamAtom);
  const playerWaiting = useAtomValue(playerWaitingAtom);
  const isAutoTestUser = retrievePersistentData<boolean>('isAutoTestUser');

  const errorDialogOpen = useAtomValue(isErrorDialogOpenAtom);
  const [remounts, setRemounts] = React.useState(0);

  React.useEffect(() => {
    setRemounts((prev) => prev + 1);
  }, [errorDialogOpen]);

  const devShowControlsOverlay =
    process.env.NODE_ENV === 'development' &&
    new URLSearchParams(window.location.search).get('showControlsOverlay') === 'true';

  const controlsVisible = (openControls && !hideControls[0]) || devShowControlsOverlay;

  const rootClass = clsx(styles['player-content'], [styles['player-content--tv']], {
    [styles['player-content--descaled']]: descalePlayer,
    [styles['player-content--no-cursor']]:
      !controlsVisible && !openChannels && !descalePlayer && !firstTimeLoading
  });

  return (
    <div
      ref={setPlayerNode}
      data-testid={testIds.player.root}
      className={rootClass}
      onMouseMove={handleMouseMove}
      onClick={IS_BROWSER ? handleClickOutside : undefined}
    >
      <div tabIndex={1} ref={rootRef} onKeyDown={handleKeyDown} />
      <div
        className={clsx(styles['player-content__overlay'], {
          [styles['player-content__overlay--visible']]: controlsVisible
        })}
      />
      <PlayerControls
        key={remounts}
        open={controlsVisible}
        onClose={handleCloseControls}
        channel={channel}
        startTime={channel?.currentProgram?.startTime}
        endTime={channel?.currentProgram?.endTime}
      />
      {streamType === StreamType.LiveStream && !descalePlayer && (
        <PlayerChannels
          open={IS_BROWSER ? openControls : openChannels}
          onClose={handleCloseChannels}
          handleChannelChange={handleChannelChange}
          visibleBrowser={IS_BROWSER && openControls}
        />
      )}
      {streamType && visualOnStream && !isAutoTestUser && (
        <FeatureIntroPlayer typeOfPlayer={mapStreamTypeToFeatureIntroLocation(streamType)} />
      )}

      {!IS_BROWSER && (
        <Fade in={(openControls || openChannels || devShowControlsOverlay) && !playerWaiting}>
          <div className={styles['player-content__clock']}>
            <Clock />
          </div>
        </Fade>
      )}
    </div>
  );
};

PlayerContent.displayName = 'PlayerContent';

export default React.memo(PlayerContent);
